import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, currentToken } from "@/lib/api";
import { EmergencyBanner } from "@/components/shared/Common";
import { Sparkles, Send, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const emergencyMatch = (t) => /999|emergency|chest pain|stroke|suicid|bleeding heavily|can'?t breath|not breathing|unconscious|severe allergic/i.test(t || "");

export default function AIIntake() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi, I'm here to help you find the right care. What's happening today?" }
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [current, setCurrent] = useState("");
  const [routing, setRouting] = useState(null);
  const scrollRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    scrollRef.current?.scrollTo(0, 999999);
  }, [messages, current]);

  const send = async () => {
    if (!input.trim() || streaming) return;
    const userMsg = { role: "user", content: input.trim() };
    if (emergencyMatch(userMsg.content)) {
      setMessages(m => [...m, userMsg, { role: "assistant", content: "🚨 What you're describing may be a medical emergency. Please call 999 (UK) or your local emergency number immediately, or go to A&E. CAREMatch is not for emergencies." }]);
      setInput("");
      return;
    }
    const newMsgs = [...messages, userMsg];
    setMessages(newMsgs);
    setInput("");
    setStreaming(true);
    setCurrent("");
    try {
      const res = await fetch(`${API}/ai/intake/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${currentToken()}` },
        body: JSON.stringify({ messages: newMsgs }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        acc += chunk;
        setCurrent(acc);
      }
      setMessages(m => [...m, { role: "assistant", content: acc }]);
      setCurrent("");
    } catch (e) {
      toast.error("AI unavailable, try again.");
    } finally { setStreaming(false); }
  };

  const finish = async () => {
    setStreaming(true);
    try {
      const transcript = messages.map(m => `${m.role.toUpperCase()}: ${m.content}`).join("\n");
      const { data } = await api.post("/ai/intake/route", { transcript });
      setRouting(data);
    } catch (e) {
      toast.error("Could not compute routing.");
    } finally { setStreaming(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-4">
        <div>
          <div className="cm-eyebrow mb-2"><Sparkles size={12} className="inline mr-1"/> AI-assisted, not AI-diagnosed</div>
          <h1 className="cm-h2">Find the right care</h1>
        </div>
        <EmergencyBanner compact/>

        <div className="cm-card flex flex-col h-[560px]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1" data-testid="intake-messages">
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm ${m.role === "user" ? "bg-[#1a2b4a] text-white" : m.content.startsWith("🚨") ? "bg-[#fef2f2] text-[#991b1b] border border-[#dc2626]/30" : "bg-[#f5f7fa] text-[#0f172a]"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {current && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-lg px-4 py-2.5 text-sm bg-[#f5f7fa] text-[#0f172a]">{current}<span className="animate-pulse">▍</span></div>
              </div>
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-[#e2e8f0] flex gap-2">
            <input className="cm-input" placeholder="Type your reply…" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e => e.key==="Enter" && send()} disabled={streaming} data-testid="intake-input"/>
            <button className="cm-btn-primary" onClick={send} disabled={streaming || !input.trim()} data-testid="intake-send"><Send size={16}/></button>
          </div>
          <button className="cm-btn-outline w-full mt-2" onClick={finish} disabled={streaming || messages.length < 2} data-testid="intake-finish">Get my care suggestion <ArrowRight size={14}/></button>
        </div>
      </div>
      <div className="space-y-4">
        <div className="cm-card">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#4a90d9] font-semibold mb-2"><AlertTriangle size={12}/> Important</div>
          <p className="text-xs text-[#475569] leading-relaxed">The AI navigator is <b>not a diagnostic tool</b>. Your doctor makes the final clinical decision. Every AI-generated summary is clearly labelled and reviewed before your consultation.</p>
        </div>
        {routing && (
          <div className="cm-card border-[#3daa6e]/40 ring-2 ring-[#3daa6e]/10" data-testid="routing-result">
            <div className="cm-eyebrow mb-2">Suggested route</div>
            {routing.urgency === "emergency" ? (
              <div className="bg-[#fef2f2] border border-[#dc2626]/30 rounded-lg p-3 text-sm text-[#991b1b]">
                <div className="font-semibold">This may be an emergency.</div>
                <div className="mt-1">Please call 999 (UK) or your local emergency number.</div>
              </div>
            ) : (
              <>
                <div className="text-lg font-bold text-[#1a2b4a] capitalize">{(routing.suggested_specialty||"").replace("_"," ")}</div>
                <div className="text-sm text-[#475569] mt-1 capitalize">{routing.consultation_type} consultation · urgency: {routing.urgency}</div>
                <p className="text-sm text-[#475569] mt-3">{routing.summary}</p>
                {routing.red_flags?.length > 0 && (
                  <div className="mt-3 text-xs bg-[#fffbeb] border border-[#d97706]/20 text-[#92400e] p-2 rounded">Red flags: {routing.red_flags.join(", ")}</div>
                )}
                <button className="cm-btn-primary w-full mt-4" onClick={() => nav(`/find-care?specialty=${routing.suggested_specialty}`)} data-testid="routing-find-doctors">Find matching doctors</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
